import { computed, Directive, inject, OnDestroy, Signal, signal, viewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { environment } from '@environments/environment';
import { FilterService } from '@modules/filter/services/filter/filter.service';
import { SEARCH_CONTROL } from '@tokens/search-contorl.token';
import { PostRequest } from '@type/request.type';
import { debounceTime, EMPTY, Observable, switchMap } from 'rxjs';
import { CompanyStateService } from 'src/app/pages/companies/services/company-state.service';

const DEFAULT_PAGINATOR = {
  pageIndex: 0,
  pageSize: environment.perPage,
};

const DEFAULT_SORTING: Sort = {
  active: '',
  direction: '',
};

@Directive({
  selector: '[tablePage]',
})
export abstract class TablePageDirective<D> implements OnDestroy {
  abstract displayedColumns: Signal<Array<keyof D | 'actions' | 'select'>>;

  protected abstract makeRequest(request: PostRequest): Observable<D[]>;

  readonly pagination = signal(DEFAULT_PAGINATOR);
  readonly sort = signal(DEFAULT_SORTING);
  readonly update = signal(Date.now());
  readonly length = signal(0);

  readonly searchControl = inject(SEARCH_CONTROL);
  readonly company = inject(CompanyStateService).company;

  protected readonly filterService = inject(FilterService);
  protected readonly search = toSignal(this.searchControl.valueChanges, { initialValue: '' });

  readonly matSort = viewChild(MatSort);
  readonly paginator = viewChild(MatPaginator);

  readonly computedRequest = computed((): PostRequest => {
    const { pageIndex, pageSize } = this.pagination();
    const sort = this.sort();

    return {
      update: this.triggerUpdate(),
      per_page: pageSize,
      sorting: this.createSortingQuery(sort),
      page: pageIndex + 1,
      q: this.buildQuery(),
    };
  });

  readonly dataSource: Signal<D[]> = toSignal(
    toObservable(this.computedRequest).pipe(
      debounceTime(500),
      switchMap((request) => (this.shouldFetch() ? this.makeRequest(request) : EMPTY)),
    ),
    { initialValue: [] as D[] },
  );

  /**
   * Override to plug in a custom update trigger signal (e.g. a service-level update counter).
   * The return value is tracked inside computedRequest, so any signal read here causes a recompute.
   */
  protected triggerUpdate(): number {
    return this.update();
  }

  /**
   * Override to build the `q` part of the request.
   * Any signals read inside this method are automatically tracked by computedRequest.
   */
  protected shouldFetch(): boolean {
    return true;
  }

  protected buildQuery(): Record<string, unknown> {
    return {};
  }

  createSortingQuery(sort: Sort): string {
    return sort.direction ? `${sort.active} ${sort.direction}` : '';
  }

  ngOnDestroy(): void {
    this.searchControl.reset('', { emitEvent: false });
  }
}
